import React from 'react';
import { Bell, User, Award, MapPin } from 'lucide-react';

interface NotificationItemProps {
    notification: {
        id: number;
        type: string;
        message: string;
        is_read: boolean;
        header?: string;
        image?: string | null;
        related_user_id?: number | null;
        related_badge_id?: number | null;
        created_at: string;
        updated_at: string;
    };
    onMarkAsRead: (id: number) => void;
    onImageClick?: (imageSrc: string, imageAlt: string) => void;
    formatDateString: (dateString: string) => string;
}

export default function NotificationItem({
    notification,
    onMarkAsRead,
    onImageClick,
    formatDateString
}: NotificationItemProps) {
    const getNotificationIcon = (type: string) => {
        switch (type) {
            case 'new_user':
                return <User className="h-4 w-4 text-secondary-gray flex-shrink-0" />;
            case 'badge_earned':
                return <Award className="h-4 w-4 text-secondary-gold flex-shrink-0" />;
            case 'nearby_cigar':
            case 'city_cigar':
                return <MapPin className="h-4 w-4 text-secondary-gray flex-shrink-0" />;
            case 'first_cigar':
                return <Award className="h-4 w-4 text-secondary-gold flex-shrink-0" />;
            default:
                return <Bell className="h-4 w-4 text-secondary-gray flex-shrink-0" />;
        }
    };

    const getNotificationLink = (notification: any) => {
        // Handle notification types differently
        switch (notification.type) {
            case 'new_user':
            case 'nearby_cigar':
            case 'city_cigar':
                if (notification.related_user_id) {
                    return `/dashboard/members`;
                }
                break;
            case 'first_cigar':
                if (notification.related_user_id) {
                    return `/dashboard/members`;
                } else {
                    return '/dashboard/journal';
                }
                break;
            case 'badge_earned':
                if (notification.related_badge_id) {
                    return `/dashboard/account`;
                }
                break;
            default:
                // Default handling
                if (notification.related_user_id) {
                    return `/dashboard/members`;
                } else if (notification.related_badge_id) {
                    return `/dashboard/account`;
                }
                break;
        }
        return null;
    };

    const link = getNotificationLink(notification);

    return (
        <div
            className={`w-full px-4 py-4 cursor-pointer ${!notification.is_read ? 'bg-primary-blue-95/20' : ''}`}
        // onClick={() => link ? (window.location.href = link) : null}
        >
            <div className="grid grid-cols-12 items-center gap-6">
                {/* Icon */}
                <div className="col-span-1">
                    <div
                        className={`w-10 h-10 rounded-full bg-primary-blue-90 flex items-center justify-center overflow-hidden ${notification.type === 'ADMIN_MESSAGE' && notification.image ? 'cursor-pointer hover:opacity-80 transition-opacity' : ''}`}
                        onClick={() => {
                            if (notification.type === 'ADMIN_MESSAGE' && notification.image && onImageClick) {
                                onImageClick(notification.image, notification.header || 'Notification Image');
                            }
                        }}
                    >
                        {notification.type === 'ADMIN_MESSAGE' && notification.image ? (
                            <img
                                src={notification.image}
                                alt={notification.header || 'Notification Image'}
                                className="w-full h-full object-cover"
                            />
                        ) : (
                            getNotificationIcon(notification.type)
                        )}
                    </div>
                </div>

                {/* Message */}
                <div className="col-span-9">
                    {notification.type === 'ADMIN_MESSAGE' && notification.header ? (
                        <div className="flex flex-col gap-1">
                            <span className={`text-base leading-6 text-white ${!notification.is_read ? 'font-bold' : 'font-bold'}`}>
                                {notification.header}
                            </span>
                            <span className={`text-base leading-6 text-white ${!notification.is_read ? 'font-normal' : 'font-normal'}`}>
                                {notification.message}
                            </span>
                        </div>
                    ) : (
                        <div className="flex items-center gap-2">
                            <span className={`text-base leading-6 text-white ${!notification.is_read ? 'font-bold' : 'font-normal'}`}>
                                {notification.message}
                            </span>
                        </div>
                    )}
                    <div className={`text-xs mt-1 ${!notification.is_read ? 'text-gray-400 font-medium' : 'text-gray-500 font-normal'}`}>
                        {formatDateString(notification.created_at)}
                    </div>
                </div>

                {/* Actions */}
                <div className="col-span-2 flex items-center justify-end">
                    {!notification.is_read && (
                        <button
                            className="text-gray-400 hover:text-gray-300 text-sm"
                            onClick={(e) => {
                                e.stopPropagation();
                                onMarkAsRead(notification.id);
                            }}
                        >
                            Mark as read
                        </button>
                    )}
                </div>
            </div>
        </div>
    );
} 